import type { Common, SegmentTrackEvent } from "@beautypie/segment";

import { isSafari } from "@shared/utils.user-agent";

import { Logger } from "lib/logger";
import { identify, page, track, getCommonFields } from "tracking/client";
import type { Track } from "tracking/client";
import { toSnakeCase } from "utils/toSnakeCase";

export const useSegment = () => {
  return {
    identify,
    page,
    track
  };
};

const DELAYED_TRACKING_EVENT_PREFIX = "track_event_";
export const delayedTrack = async (
  event: SegmentTrackEvent,
  contextOverrides?: Partial<Common["context"]>
) => {
  const eventToSnakeCase = toSnakeCase(event.event);
  sessionStorage.setItem(
    `${DELAYED_TRACKING_EVENT_PREFIX + eventToSnakeCase}_${Date.now()}`,
    JSON.stringify([
      event,
      await getCommonFields(contextOverrides).then(({ context }) => context)
    ])
  );
};

export const trackClick = isSafari() ? delayedTrack : track;

const sendDelayedTrackEvent = async (key: string): Promise<void> => {
  if (!window?.sessionStorage) {
    Logger.warn(
      `sendDelayedTrackEvent(): Expected "window.sessionStorage" to be available, but it was not`
    );
    return;
  }
  try {
    const item = window.sessionStorage.getItem(key) ?? "";
    if (!item) {
      return;
    }
    const args: Parameters<Track> = JSON.parse(item);
    if (Array.isArray(args)) {
      track(...args);
    }

    window.sessionStorage.removeItem(key);
  } catch (err: unknown) {
    Logger.warn(`Event not processed, due to error: "${err}"`);
  }
};

export const sendDelayedTrackEvents = (): void => {
  if (!window.sessionStorage) {
    return;
  }

  const queuedTrackingEventKeys = Object.keys(window.sessionStorage).filter(
    (key) => key.startsWith(`track_event_`)
  );
  queuedTrackingEventKeys.forEach((key) => sendDelayedTrackEvent(key));
};
